/**
 * Admin API — endpoints /admin/* del backend.
 */
import { api } from "./client";

export interface AdminMetrics {
  users_total: number;
  users_active_30d: number;
  users_new_7d: number;
  trades_total: number;
  trades_completed: number;
  trades_pending: number;
  listings_active: number;
  reviews_avg_stars: number | null;
  reports_open: number;
}

export interface AdminUserRow {
  id: number;
  name: string;
  handle: string | null;
  city: string | null;
  country: string;
  region: string | null;
  avatar_color: string;
  initials: string;
  verified: boolean;
  stars_avg: number;
  trades_completed: number;
  level: number;
  email: string;
  role: string;
  is_active: boolean;
  is_banned: boolean;
  created_at: string;
  last_seen_at: string | null;
  album_pct: number;
  album_dups: number;
}

export interface AdminTradeRow {
  id: number;
  from_user_id: number;
  to_user_id: number;
  status: string;
  created_at: string;
  completed_at: string | null;
}

export interface AdminListingRow {
  id: number;
  seller_id: number;
  sticker_id: number;
  type: string;
  price: number;
  current_bid: number | null;
  ends_at: string | null;
  created_at: string;
}

export interface AdminAuditRow {
  id: number;
  admin_user_id: number;
  action: string;
  target_type: string | null;
  target_id: string | null;
  payload: unknown;
  created_at: string;
}

export async function fetchMetrics(): Promise<AdminMetrics> {
  return api<AdminMetrics>("/admin/metrics");
}

export async function fetchUsers(opts: { q?: string; role?: string; limit?: number; offset?: number } = {}): Promise<AdminUserRow[]> {
  const params = new URLSearchParams();
  if (opts.q) params.set("q", opts.q);
  if (opts.role) params.set("role", opts.role);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return api<AdminUserRow[]>(`/admin/users${qs ? `?${qs}` : ""}`);
}

export async function fetchRecentTrades(limit = 20): Promise<AdminTradeRow[]> {
  return api<AdminTradeRow[]>(`/admin/trades/recent?limit=${limit}`);
}

export async function fetchActiveListings(limit = 50): Promise<AdminListingRow[]> {
  return api<AdminListingRow[]>(`/admin/listings/active?limit=${limit}`);
}

export async function fetchAuditLog(limit = 50): Promise<AdminAuditRow[]> {
  return api<AdminAuditRow[]>(`/admin/audit-log?limit=${limit}`);
}

export async function banUser(userId: number, reason: string): Promise<void> {
  await api(`/admin/users/${userId}/ban?reason=${encodeURIComponent(reason)}`, { method: "POST" });
}

export async function unbanUser(userId: number): Promise<void> {
  await api(`/admin/users/${userId}/unban`, { method: "POST" });
}

export async function deleteUser(userId: number): Promise<void> {
  await api(`/admin/users/${userId}`, { method: "DELETE" });
}

// ---- Edición del catálogo ----

export interface StickerPatch {
  name?: string;
  role?: string;
  is_legendary?: boolean;
  is_limited?: boolean;
  shirt?: number;
  image_url?: string;
}

export interface StickerOut {
  id: number;
  num: string;
  team_id: number;
  name: string;
  role: string;
  position: number;
  is_shield: boolean;
  is_legendary: boolean;
  is_limited: boolean;
  is_coca_cola: boolean;
  shirt: number | null;
  color: string | null;
  image_url: string | null;
}

export async function patchSticker(id: number, payload: StickerPatch): Promise<StickerOut> {
  return api<StickerOut>(`/admin/stickers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export interface BulkUpdateItem {
  num?: string;
  id?: number;
  name?: string;
  role?: string;
  is_legendary?: boolean;
  shirt?: number;
}

export interface BulkUpdateResult {
  updated: number;
  skipped: number;
  errors: string[];
}

export async function bulkUpdateStickers(items: BulkUpdateItem[]): Promise<BulkUpdateResult> {
  return api<BulkUpdateResult>(`/admin/stickers/bulk-update`, {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

// ---- Reports (estadísticas, no denuncias) ----

export interface ReportsGlobalSummary {
  users_active: number;
  avg_album_pct: number;
  total_owned: number;
  total_dups: number;
  catalog_size: number;
}

export interface CountryReportRow {
  country: string;
  users_count: number;
  avg_album_pct: number;
  total_dups: number;
}

export interface TopCollectorRow {
  user_id: number;
  name: string;
  handle: string | null;
  country: string;
  album_pct: number;
  owned: number;
}

export interface StickerReportRow {
  sticker_id: number;
  sticker_num: string;
  sticker_name: string;
  team_name: string;
  users_count: number;
}

export async function fetchReportsGlobalSummary(): Promise<ReportsGlobalSummary> {
  return api<ReportsGlobalSummary>(`/admin/reports/global-summary`);
}

export async function fetchReportsByCountry(): Promise<CountryReportRow[]> {
  return api<CountryReportRow[]>(`/admin/reports/by-country`);
}

export async function fetchTopCollectors(limit = 20): Promise<TopCollectorRow[]> {
  return api<TopCollectorRow[]>(`/admin/reports/top-collectors?limit=${limit}`);
}

export async function fetchMostNeeded(limit = 20): Promise<StickerReportRow[]> {
  return api<StickerReportRow[]>(`/admin/reports/most-needed?limit=${limit}`);
}

export async function fetchMostDuped(limit = 20): Promise<StickerReportRow[]> {
  return api<StickerReportRow[]>(`/admin/reports/most-duped?limit=${limit}`);
}
